import videos from '../../data/videos.json';
import type { AppDef, AppContext } from '../../types/window';
import type { VideoClip } from '../../types/content';
import { el, button } from './dom';

const catalog = videos as VideoClip[];
const syncers = new WeakMap<HTMLElement, (ctx: AppContext) => void>();

export function resolveClip(id: unknown): VideoClip | undefined {
  if (typeof id !== 'string' || !id) return undefined;
  return catalog.find((v) => v.id === id);
}

function renderList(root: HTMLElement, ctx: AppContext): void {
  ctx.setTitle('Videos');
  const wrap = el('div', { className: 'app-stack' });
  wrap.append(
    el('h1', { text: 'Videos' }),
    el('p', {
      text: catalog.length
        ? 'Short cartoons. Click Play.'
        : 'No clips yet. Drop an .mp4 in public/videos/ and add a row to src/data/videos.json.',
    }),
  );
  for (const v of catalog) {
    const row = el('div', { className: 'app-row' });
    const text = el('div');
    text.append(el('strong', { text: v.title }));
    if (v.summary) text.append(el('div', { text: v.summary }));
    const actions = el('div');
    actions.append(button('Play', () => ctx.openApp('videos', { videoId: v.id })));
    row.append(text, actions);
    wrap.append(row);
  }
  root.replaceChildren(wrap);
}

function renderPlay(root: HTMLElement, ctx: AppContext, clip: VideoClip, onExit: () => void): void {
  ctx.setTitle(clip.title);
  const stage = el('div', { className: 'video-stage' });
  const bar = el('div', { className: 'game-toolbar' });
  bar.append(button('Library', onExit), el('p', { text: clip.summary ?? clip.title }));
  const player = document.createElement('video');
  player.className = 'video-el';
  player.controls = true;
  player.playsInline = true;
  player.preload = 'metadata';
  player.src = clip.src;
  if (clip.poster) player.poster = clip.poster;
  stage.append(bar, player);
  root.replaceChildren(stage);
  queueMicrotask(() => {
    void player.play().catch(() => undefined);
  });
}

export const videosApp: AppDef = {
  appId: 'videos',
  title: 'Videos',
  icon: 'videos',
  defaultSize: { w: 640, h: 480 },
  minSize: { w: 360, h: 280 },
  singleton: true,
  mount(root, ctx) {
    let playingId: string | null = null;

    const exitPlay = (): void => {
      playingId = null;
      renderList(root, ctx);
      ctx.openApp('videos', { videoId: '' });
    };

    const sync = (nextCtx: AppContext): void => {
      const next = resolveClip(nextCtx.payload?.videoId);
      if (next && next.id === playingId) return;
      if (next) {
        playingId = next.id;
        renderPlay(root, nextCtx, next, exitPlay);
        return;
      }
      if (playingId) return;
      renderList(root, nextCtx);
    };

    syncers.set(root, sync);
    sync(ctx);
    return () => {
      playingId = null;
      syncers.delete(root);
      root.replaceChildren();
    };
  },
  update(root, ctx) {
    syncers.get(root)?.(ctx);
  },
};
